import { AnalogInput, Buzzer, Comparator, ConditionalOperator, GuardZone, HeatingCircuit, LogicExpression, Pause, Pump, RadioSensor, RadioSensorValue, RelayControl, SensorReading, StopScenario, ThreewayTap, } from 'z3k-config';
import { BlocklyError } from '../../utils/blockly-error';
import { FixedBlockType } from '../../types/block-type';
import { assertNever } from 'webui/utils/never';
import { addTimeComparatorToTimeComparators, addWeekdaysToTimeComparators, timeUnitsDict, } from 'console/blockly/utils/time';
import { commandReactionTranslate, setGuardReactionTranslate, threewayTapReactionTranslate, } from 'console/blockly/utils/translates';
import { findOrCreateCommandInConfig } from 'z3k-parser/lib/command';
import { HeatingMode } from 'z3k-parser/model/z3k-config/20-heating-mode';
/**
 * @function
 * 2 шаг сохранения
 * Из Blockly json в Z3KObject
 * @param config - Z3KConfig устройства
 * @param block - Blockly json
 * @param ids - ссылки сценария
 * @param new_id - новый id
 * @return [Z3KConfig, ScenarioLinkId[]] - обновлённый конфиг и ссылки
 */
export function simpleDoB2C(config, block, ids, new_id) {
    let newConfig = config;
    let newIds = [...ids, new_id];
    if (block.next) {
        [newConfig, newIds] = resolverB2C(newConfig, block.next, newIds);
    }
    return [newConfig, newIds];
}
function breakB2C(config, block, ids) {
    const [new_id, newConfig] = StopScenario.get.add(config, {
        type: StopScenario.Type.BREAK,
        return_id: null,
    });
    return simpleDoB2C(newConfig, block, ids, new_id);
}
function continueB2C(config, block, ids) {
    const [new_id, newConfig] = StopScenario.get.add(config, {
        type: StopScenario.Type.CONTINUE,
        return_id: null,
    });
    return simpleDoB2C(newConfig, block, ids, new_id);
}
function returnB2C(config, block, ids) {
    const [new_id, newConfig] = StopScenario.get.add(config, {
        type: StopScenario.Type.RETURN,
        return_id: null,
    });
    return simpleDoB2C(newConfig, block, ids, new_id);
}
function doScenarioB2C(config, block, ids) {
    return simpleDoB2C(config, block, ids, block.scenario);
}
function generateCommandNameForHeatingCircuit(config, block) {
    const mode_name = HeatingMode.get.byId(config, block.mode)?.name;
    const circuit_name = block.for === 'all'
        ? 'всех контуров'
        : HeatingCircuit.get.byId(config, block.circuit)?.name;
    return `Активировать режим отопления ${mode_name} для ${circuit_name}`;
}
function doHeatModeForHeatCircuitB2C(config, block, ids) {
    let command;
    switch (block.for) {
        case 'all':
            command = {
                objectid: block.mode,
                cmd: '1',
                name: generateCommandNameForHeatingCircuit(config, block),
            };
            break;
        case 'single':
            command = {
                objectid: block.circuit,
                cmd: block.mode.toString(),
                name: generateCommandNameForHeatingCircuit(config, block),
            };
            break;
        default:
            assertNever(block);
    }
    const [command_id, config_with_command] = findOrCreateCommandInConfig(config, command);
    let result_config = config_with_command;
    let newIds = [...ids, command_id];
    if (block.next) {
        [result_config, newIds] = resolverB2C(result_config, block.next, newIds);
    }
    return [result_config, newIds];
}
function andOrB2C(config, block, ids) {
    if (block.clauses == null) {
        return [config, ids];
    }
    const [new_config, comparator_ids] = block.clauses.reduce(([prev_config, prev_ids], block) => {
        const [new_config, new_comparator_id] = resolverB2C(prev_config, block, []);
        return [new_config, [...prev_ids, ...new_comparator_id]];
    }, [config, []]);
    const [new_id, result_config] = LogicExpression.get.add(new_config, {
        type: block.type === FixedBlockType.or
            ? LogicExpression.Type.OR
            : LogicExpression.Type.AND,
        conditions: comparator_ids,
    });
    return [result_config, [...ids, new_id]];
}
function addNext(config, new_ids, block) {
    //Добавляем следующий элемент после условия
    if ('next' in block && block.next) {
        return resolverB2C(config, block.next, new_ids);
    }
    return [config, new_ids];
}
export function ifThenElseB2C(config, block, ids) {
    let statementElse = [];
    //Добавляем сравниватель
    const [config_plus_comparator, comparator_ids] = resolverB2C({ ...config }, block.comparator, []);
    //Добавляем выполнение в случае ДА
    const [config_plus_then, statement_then] = resolverB2C(config_plus_comparator, block.then, []);
    let config_plus_else = { ...config_plus_then };
    //Добавляем выполнение в случае НЕТ
    if (block.type === FixedBlockType.if_then_else) {
        [config_plus_else, statementElse] = resolverB2C(config_plus_then, block.else, []);
    }
    //в конфиг записываем
    const [new_id, config_plus_cond_operator] = ConditionalOperator.get.add(config_plus_else, {
        type: block.type === FixedBlockType.if_then ||
            block.type === FixedBlockType.if_run
            ? ConditionalOperator.Type.IF_THEN
            : ConditionalOperator.Type.IF_THEN_ELSE,
        comparator_id: comparator_ids[0],
        statementThen: statement_then,
        statementElse: block.type === FixedBlockType.if_then_else ? statementElse : [],
    });
    return addNext(config_plus_cond_operator, [...ids, new_id], block);
}
function notB2C(config, block, ids) {
    const [new_config, conditionsIds] = resolverB2C(config, block.inp, []);
    const [new_id, result_config] = LogicExpression.get.add(new_config, {
        type: LogicExpression.Type.NOT,
        conditions: conditionsIds,
    });
    return [result_config, [...ids, new_id]];
}
/**
 * @function
 * @param config - конфиг устройства
 * @param block - блок сравнения с числом
 * @return number
 * возвращаю константy, домноженную на нужный коэффициент, чтобы верно сравнивать
 */
function getMultiplyConst(config, block) {
    if (block.left == null) {
        return 0;
    }
    switch (block.left.type) {
        case FixedBlockType.radio_input: {
            const type = RadioSensor.get.byId(config, block.left.id)?.type ??
                RadioSensor.Type.TEMPERATURE_A;
            if (block.left.value_type === RadioSensorValue.BATTERY_LEVEL) {
                return block.right;
            }
            if (block.left.value_type === RadioSensorValue.RSSI) {
                return block.right;
            }
            if (type === RadioSensor.Type.HUMIDITY) {
                return block.right;
            }
            return (block.right + 273) * 10;
        }
        case FixedBlockType.temperature_input:
            //    вернуть в дециКельвинах
            return (block.right + 273) * 10;
        case FixedBlockType.analog_input: {
            const object = AnalogInput.get.byId(config, block.left.id);
            return ((object != null ? AnalogInput.getMultiplier(object) : 1) * block.right);
        }
        default:
            return 0;
    }
}
function numericComparatorB2C(config, block, ids) {
    const [new_config, leftIds] = resolverB2C(config, block.left, []);
    const [new_id, result_config] = Comparator.get.add(new_config, {
        type: block.comparator_type,
        left_object_id: leftIds[0],
        right_object_id: null,
        right_const: getMultiplyConst(new_config, block),
    });
    return [result_config, [...ids, new_id]];
}
function sensorComparatorB2C(config, block, ids) {
    const [new_config, leftIds] = resolverB2C(config, block.left, []);
    const [new_config_with_right, rightIds] = resolverB2C(new_config, block.right, leftIds);
    const [new_id, result_config] = Comparator.get.add(new_config_with_right, {
        type: block.comparator_type,
        left_object_id: rightIds[0],
        right_object_id: rightIds[1],
        right_const: 0,
    });
    return [result_config, [...ids, new_id]];
}
function sensorReadingB2C(config, block, ids) {
    let argument = 0;
    if (block.type === FixedBlockType.radio_input) {
        argument = block.value_type;
    }
    const [new_id, result_config] = SensorReading.get.add(config, {
        obj_id: block.id,
        type: SensorReading.Type.VALUE,
        argument,
    });
    return [result_config, [...ids, new_id]];
}
function sensorStateB2C(config, block, ids) {
    const [new_id, result_config] = SensorReading.get.add(config, {
        type: SensorReading.Type.STATE,
        argument: block.argument,
        obj_id: block.id,
    });
    return [result_config, [...ids, new_id]];
}
function pauseB2C(config, block, ids) {
    const [new_id, newConfig] = Pause.get.add(config, {
        time: block.value * timeUnitsDict[block.unit_time],
    });
    return simpleDoB2C(newConfig, block, ids, new_id);
}
function timeComparatorB2C(config, block, ids) {
    const [new_id, result_config] = addTimeComparatorToTimeComparators(config, block.comparator_type, block.hour, block.min);
    return [result_config, [...ids, new_id]];
}
function weekdaysB2C(config, block, ids) {
    const [new_id, result_config] = addWeekdaysToTimeComparators(config, block.weekday_register);
    return [result_config, [...ids, new_id]];
}
function findOrCreateCommand(config, block) {
    if ('target_temp' in block) {
        return findOrCreateCommandInConfig(config, {
            objectid: block.circuit,
            //вернуть в дециКельвинах
            cmd: ((block.target_temp + 273) * 10).toString(),
            name: generateCommandName(config, block),
        });
    }
    return findOrCreateCommandInConfig(config, {
        objectid: block.id,
        cmd: block.argument.toString(),
        name: generateCommandName(config, block),
    });
}
function generateCommandName(config, block) {
    switch (block.type) {
        case FixedBlockType.do_pump: {
            const name = Pump.get.byId(config, block.id)?.name ?? '1';
            return `Насос ${name} ${commandReactionTranslate[block.argument]}`;
        }
        case FixedBlockType.do_relay: {
            const name = RelayControl.get.byId(config, block.id)?.name ?? '1';
            return `Реле ${name} ${commandReactionTranslate[block.argument]}`;
        }
        case FixedBlockType.set_guard: {
            const name = GuardZone.get.byId(config, block.id)?.name ?? '1';
            return `Охранную зону ${name} ${setGuardReactionTranslate[block.argument]}`;
        }
        case FixedBlockType.do_buzzer: {
            const name = Buzzer.get.byId(config, block.id)?.name ?? '1';
            return `Сирена ${name} ${commandReactionTranslate[block.argument]}`;
        }
        case FixedBlockType.do_tap: {
            const tap_name = ThreewayTap.get.byId(config, block.id)?.name ?? '1';
            return `Кран ${tap_name} ${threewayTapReactionTranslate[block.argument]}`;
        }
        case FixedBlockType.do_temperature_in_circuit: {
            const name = HeatingCircuit.get.byId(config, block.circuit)?.name ?? '1';
            return `Установить целевую температуру ${block.target_temp} для контура ${name}`;
        }
        default:
            throw new BlocklyError('generateCommandName', 'не знаю такой тип блока');
    }
}
/**
 * @function
 * 2 шаг сохранения
 * Из Blockly json в Z3KObject
 * @param config - Z3KConfig устройства
 * @param block - Blockly json
 * @param ids - ссылки сценария
 * @return [Z3KConfig, ScenarioLinkId[]] - обновлённый конфиг и ссылки
 */
export function doWithCommandB2C(config, block, ids) {
    const [command_id, new_config] = findOrCreateCommand(config, block);
    if (!command_id)
        throw new BlocklyError('doWithCommandB2C', 'не нашёл нужную команду');
    return simpleDoB2C(new_config, block, ids, command_id);
}
export function resolverB2C(config, block, ids) {
    const new_config = { ...config };
    if (block === null) {
        return [new_config, []];
    }
    const type = block.type;
    switch (type) {
        //run - обрабатывается отдельно
        case FixedBlockType.linear:
        case FixedBlockType.if_run:
        case FixedBlockType.loop:
        case FixedBlockType.schedule_run:
            return [new_config, ids];
        //logic
        case FixedBlockType.and:
        case FixedBlockType.or:
            return andOrB2C(new_config, block, ids);
        case FixedBlockType.not:
            return notB2C(new_config, block, ids);
        case FixedBlockType.if_then:
        case FixedBlockType.if_then_else:
            return ifThenElseB2C(new_config, block, ids);
        case FixedBlockType.numeric_comparator:
            return numericComparatorB2C(new_config, block, ids);
        case FixedBlockType.sensor_comparator:
            return sensorComparatorB2C(new_config, block, ids);
        //do
        case FixedBlockType.do_buzzer:
        case FixedBlockType.do_pump:
        case FixedBlockType.do_relay:
        case FixedBlockType.do_tap:
        case FixedBlockType.do_temperature_in_circuit:
        case FixedBlockType.set_guard:
            return doWithCommandB2C(new_config, block, ids);
        case FixedBlockType.do_output_action:
        case FixedBlockType.do_notification:
            return simpleDoB2C(new_config, block, ids, 'id' in block ? block.id : 0);
        case FixedBlockType.pause:
            return pauseB2C(new_config, block, ids);
        case FixedBlockType.do_scenario:
            return doScenarioB2C(new_config, block, ids);
        case FixedBlockType.do_return:
            return returnB2C(new_config, block, ids);
        case FixedBlockType.do_break:
            return breakB2C(new_config, block, ids);
        case FixedBlockType.do_continue:
            return continueB2C(new_config, block, ids);
        // sensor-reading
        case FixedBlockType.analog_input:
        case FixedBlockType.radio_input:
        case FixedBlockType.temperature_input:
            return sensorReadingB2C(new_config, block, ids);
        //activate-heating-modes
        case FixedBlockType.do_heat_mode_for_heat_circuit:
            return doHeatModeForHeatCircuitB2C(new_config, block, ids);
        // sensor-states
        case FixedBlockType.analog_input_state:
        case FixedBlockType.analog_temperature_sensor_state:
        case FixedBlockType.state_heat_mode_for_heat_circuit:
        case FixedBlockType.guard_state:
        case FixedBlockType.pump_state:
        case FixedBlockType.radio_sensor_state:
        case FixedBlockType.radio_sensor_433_state:
        case FixedBlockType.relay_state:
        case FixedBlockType.buzzer_state:
        case FixedBlockType.tap_state:
        case FixedBlockType.wired_temperature_sensor_state:
        case FixedBlockType.boiler_adapter:
            return sensorStateB2C(new_config, block, ids);
        //time
        case FixedBlockType.time_comparator:
            return timeComparatorB2C(new_config, block, ids);
        case FixedBlockType.weekdays_input:
            return weekdaysB2C(new_config, block, ids);
        default:
            throw new BlocklyError('resolverB2C', `преобразователь для ${block.type} неопределён`);
    }
}
